import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { EASE, Overline, Reveal, scrollToId } from "./Reveal";

const ENTITIES = [
  {
    id: "tech",
    num: "01",
    name: "Dignifyd Tech",
    role: "Technology delivery",
    desc: "Infrastructure, cloud, applications and managed IT for the enterprise estate — assessed, built, deployed and supported under one delivery standard.",
    tags: ["Cloud services", "Managed IT", "Web & mobile", "Big data & analytics"],
    metric: "1,500+ projects · 98% satisfaction",
    site: "https://www.dignifyd.tech",
    span: "md:col-span-7",
  },
  {
    id: "talent",
    num: "02",
    name: "Talent Connect",
    role: "Workforce delivery",
    desc: "Managed talent acquisition, GCC & capability building and talent intelligence — verified, deployable capability across 35+ countries.",
    tags: ["Niche & leadership search", "Volume hiring", "GCC build-operate-transfer"],
    metric: "36h first submissions · 92% day-90 retention",
    site: "https://dignifydtalentconnect.com",
    span: "md:col-span-5",
  },
  {
    id: "digital",
    num: "03",
    name: "Dignifyd Digital",
    role: "Brand & digital",
    desc: "Digital strategy, marketing, branding and creative for the same enterprises the other four entities serve.",
    tags: ["Digital strategy", "Demand generation", "Brand identity", "Creative production"],
    metric: "40+ programmes delivered",
    site: "https://www.dignifyd.digital",
    span: "md:col-span-5",
  },
  {
    id: "enterprise",
    num: "04",
    name: "Enterprise Solutions",
    role: "ERP, cloud & infrastructure",
    desc: "ERP implementation, cloud delivery and enterprise architecture advisory for BFSI, technology and manufacturing.",
    tags: ["ERP implementation", "Enterprise architecture", "Business enablement"],
    metric: "6 shared delivery centres",
    span: "md:col-span-7",
  },
  {
    id: "datalabs",
    num: "05",
    name: "Dignifyd Data Labs",
    role: "Data & applied AI",
    desc: "Data engineering, applied AI/ML and intelligent decision systems — the team behind the Group's own AI matching engine, applied to your data challenges.",
    tags: ["Data engineering", "Applied AI/ML", "Workforce & business analytics", "Decision systems"],
    metric: "AI scoring engine, proven in production",
    span: "md:col-span-12",
  },
];

export default function Entities() {
  return (
    <section id="entities" data-testid="entities-section" className="mx-auto max-w-7xl scroll-mt-24 px-6 py-24 lg:px-8 lg:py-32">
      <Reveal>
        <Overline>The Group</Overline>
        <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <h2 className="max-w-xl font-heading text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl">
            Five entities. <span className="text-gold">One delivery chain.</span>
          </h2>
          <p className="max-w-md text-base leading-relaxed text-gray-400">
            Each entity keeps its specialism. All five coordinate as a single partner —
            one programme, four handovers removed.
          </p>
        </div>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12">
        {ENTITIES.map((e, i) => (
          <motion.article
            key={e.id}
            data-testid={`entity-card-${e.id}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: (i % 2) * 0.12, ease: EASE }}
            onClick={() => scrollToId("#contact")}
            className={`group relative cursor-pointer overflow-hidden border border-white/10 bg-surface/50 p-6 transition-colors duration-500 hover:border-gold/50 hover:bg-surface sm:p-8 lg:p-10 ${e.span}`}
          >
            <div
              className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-blue-500/0 blur-3xl transition-[background-color] duration-700 group-hover:bg-blue-500/10"
              aria-hidden
            />
            <div className="flex items-start justify-between gap-4">
              <p className="font-mono text-xs tracking-[0.25em] text-gray-500 transition-colors duration-300 group-hover:text-gold">
                {e.num}
              </p>
              <ArrowUpRight className="h-5 w-5 text-gray-600 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-gold" />
            </div>
            <h3 className="mt-8 font-heading text-2xl font-medium tracking-tight text-white sm:text-3xl">
              {e.name}
            </h3>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
              {e.role}
            </p>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-gray-400">{e.desc}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {e.tags.map((t) => (
                <span
                  key={t}
                  className="border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-gray-400 transition-colors duration-300 group-hover:border-white/20"
                >
                  {t}
                </span>
              ))}
            </div>
            <p className="mt-8 border-t border-white/10 pt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-gray-500">
              {e.metric}
            </p>
            {e.site && (
              <a
                href={e.site}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`entity-link-${e.id}`}
                onClick={(ev) => ev.stopPropagation()}
                className="group/link mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:text-blue-300"
              >
                Visit {e.site.replace("https://", "").replace("www.", "")}
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
              </a>
            )}
          </motion.article>
        ))}
      </div>
    </section>
  );
}
